import React, { useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Drawer, Button, Grid, Dropdown, Modal, Form, Input, message, Tag, ConfigProvider, Spin } from 'antd';
import esES from 'antd/locale/es_ES';
import {
  TeamOutlined, SolutionOutlined, TrophyOutlined, UserOutlined, EnvironmentOutlined, FileTextOutlined,
  GlobalOutlined, HomeOutlined, ContactsOutlined, AuditOutlined, MenuOutlined,
  LogoutOutlined, KeyOutlined, SafetyOutlined, SkinOutlined, ReadOutlined
} from '@ant-design/icons';

import { AuthProvider, useAuth } from './context/AuthContext';
import api from './services/api';
import Login from './pages/Login';
import './App.css';

const Home = lazy(() => import('./pages/Home'));
const Partidos = lazy(() => import('./pages/Partidos'));
const PartidoDetalle = lazy(() => import('./pages/PartidoDetalle'));
const PartidoForm = lazy(() => import('./pages/PartidoForm'));
const Jugadores = lazy(() => import('./pages/Jugadores'));
const JugadorDetalle = lazy(() => import('./pages/JugadorDetalle'));
const DTDetalle = lazy(() => import('./pages/DTDetalle'));
const Personas = lazy(() => import('./pages/Personas'));
const PersonaDetalle = lazy(() => import('./pages/PersonaDetalle'));
const Equipos = lazy(() => import('./pages/Equipos'));
const EquipoDetalle = lazy(() => import('./pages/EquipoDetalle'));
const Campeonatos = lazy(() => import('./pages/Campeonatos'));
const CampeonatoDetalle = lazy(() => import('./pages/CampeonatoDetalle'));
const Paises = lazy(() => import('./pages/Paises'));
const PaisDetalle = lazy(() => import('./pages/PaisDetalle'));
const Ciudades = lazy(() => import('./pages/Ciudades'));
const Estadios = lazy(() => import('./pages/Estadios'));
const EstadioDetalle = lazy(() => import('./pages/EstadioDetalle'));
const EstadiosMapa = lazy(() => import('./pages/EstadiosMapa'));
const Arbitros = lazy(() => import('./pages/Arbitros'));
const ArbitroDetalle = lazy(() => import('./pages/ArbitroDetalle'));
const PartidosArbitro = lazy(() => import('./pages/PartidosArbitro'));
const UbicacionEstadios = lazy(() => import('./pages/UbicacionEstadios'));
const UrlsRecursos = lazy(() => import('./pages/UrlsRecursos'));
const DirectoresTecnicos = lazy(() => import('./pages/DirectoresTecnicos'));
const DirectoresTecnicosAtilio = lazy(() => import('./pages/DirectoresTecnicosAtilio'));
const MotivosPartido = lazy(() => import('./pages/MotivosPartido'));
const Posiciones = lazy(() => import('./pages/Posiciones'));
const NombresCampeonato = lazy(() => import('./pages/NombresCampeonato'));
const Seguridad = lazy(() => import('./pages/Seguridad'));
const Parametros = lazy(() => import('./pages/Parametros'));
const PartidosSinAtilio = lazy(() => import('./pages/PartidosSinAtilio'));
const AsignarCampeonatoAdicional = lazy(() => import('./pages/AsignarCampeonatoAdicional'));
const JugadoresRivales = lazy(() => import('./pages/JugadoresRivales'));
const JugadorRivalDetalle = lazy(() => import('./pages/JugadorRivalDetalle'));
const DTsRivales = lazy(() => import('./pages/DTsRivales'));
const DTRivalDetalle = lazy(() => import('./pages/DTRivalDetalle'));
const Camisetas = lazy(() => import('./pages/Camisetas'));
const CamisetaDetalle = lazy(() => import('./pages/CamisetaDetalle'));
const Marcas = lazy(() => import('./pages/Marcas'));
const Sponsors = lazy(() => import('./pages/Sponsors'));
const AsignarSponsors = lazy(() => import('./pages/AsignarSponsors'));
const AsignarCamiseta = lazy(() => import('./pages/AsignarCamiseta'));
const MantenimientoRecursos = lazy(() => import('./pages/MantenimientoRecursos'));
const Biblioteca = lazy(() => import('./pages/Biblioteca'));
const BibliotecaDetalle = lazy(() => import('./pages/BibliotecaDetalle'));
const BibliotecaNumeroDetalle = lazy(() => import('./pages/BibliotecaNumeroDetalle'));

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <Spin size="large" />
  </div>
);

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;

const navMenuItems = [
  { key: '/', icon: <HomeOutlined />, label: 'Inicio', path: '/' },
  { key: '/partidos', icon: <FileTextOutlined />, label: 'Partidos', path: '/partidos' },
  { key: '/jugadores', icon: <UserOutlined />, label: 'Jugadores', path: '/jugadores' },
  { key: '/directores-tecnicos', icon: <UserOutlined />, label: 'D. Técnicos', path: '/directores-tecnicos' },
  { key: '/jugadores-rivales', icon: <TeamOutlined />, label: 'Jugadores Rivales', path: '/jugadores-rivales', requireEdit: true },
  { key: '/dts-rivales', icon: <SolutionOutlined />, label: 'DTs Rivales', path: '/dts-rivales', requireEdit: true },
  { key: '/equipos', icon: <TeamOutlined />, label: 'Equipos', path: '/equipos' },
  { key: '/estadios', icon: <EnvironmentOutlined />, label: 'Estadios', path: '/estadios' },
  { key: '/campeonatos', icon: <TrophyOutlined />, label: 'Campeonatos', path: '/campeonatos' },
  { key: '/arbitros', icon: <AuditOutlined />, label: 'Árbitros', path: '/arbitros' },
  { key: '/camisetas', icon: <SkinOutlined />, label: 'Camisetas', path: '/camisetas' },
  { key: '/biblioteca', icon: <ReadOutlined />, label: 'Biblioteca', path: '/biblioteca' },
  { key: '/personas', icon: <ContactsOutlined />, label: 'Personas', path: '/personas', requireEdit: true },
  { key: '/paises', icon: <GlobalOutlined />, label: 'Países', path: '/paises', requireEdit: true },
  {
    key: 'config', icon: <EnvironmentOutlined />, label: 'Configuración', requireEdit: true,
    children: [
      { key: '/motivos-partido', label: 'Motivos Partido', path: '/motivos-partido' },
      { key: '/posiciones', label: 'Posiciones', path: '/posiciones' },
      { key: '/nombres-campeonato', label: 'Nombres Campeonato', path: '/nombres-campeonato' },
      { key: '/partidos-arbitro', label: 'Árbitros por Partido', path: '/partidos-arbitro' },
      { key: '/ubicacion-estadios', label: 'Ubicación de Estadios', path: '/ubicacion-estadios', requireEdit: true },
      { key: '/urls-recursos', label: 'URLs de Recursos', path: '/urls-recursos', requireEdit: true },
      { key: '/partidos-sin-atilio', label: 'Partidos sin AtilioId', path: '/partidos-sin-atilio', requireEdit: true },
      { key: '/asignar-campeonato-adicional', label: 'Campeonato Adicional', path: '/asignar-campeonato-adicional', requireEdit: true },
      { key: '/marcas', label: 'Marcas de Camiseta', path: '/marcas', requireEdit: true },
      { key: '/sponsors', label: 'Sponsors', path: '/sponsors', requireEdit: true },
      { key: '/asignar-sponsors', label: 'Asignar Sponsors', path: '/asignar-sponsors', requireEdit: true },
      { key: '/asignar-camiseta', label: 'Asignar Camiseta a Partidos', path: '/asignar-camiseta', requireEdit: true },
      { key: '/mantenimiento-recursos', label: 'Mantenimiento Recursos', path: '/mantenimiento-recursos', requireEdit: true },
      { key: '/parametros', label: 'Parámetros', path: '/parametros', requireSuperAdmin: true }
    ]
  }
];

const renderMenuItems = (items, onItemClick, isSuperAdmin, canEdit) => items.map(item => {
  if (item.children) {
    const visibleChildren = item.children.filter(child =>
      (!child.requireSuperAdmin || isSuperAdmin) &&
      (!child.requireEdit || canEdit)
    );
    return {
      key: item.key, icon: item.icon, label: item.label,
      children: visibleChildren.map(child => ({
        key: child.key,
        label: <Link to={child.path} onClick={onItemClick}>{child.label}</Link>
      }))
    };
  }
  return { key: item.key, icon: item.icon, label: <Link to={item.path} onClick={onItemClick}>{item.label}</Link> };
});

function ChangePasswordModal({ open, onClose }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('Las contraseñas nuevas no coinciden');
      return;
    }
    setLoading(true);
    try {
      await api.put('/auth/change-password', { currentPassword: values.currentPassword, newPassword: values.newPassword });
      message.success('Contraseña actualizada exitosamente');
      form.resetFields();
      onClose();
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cambiar contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Cambiar Contraseña" open={open} onCancel={onClose} onOk={() => form.submit()} okText="Cambiar" cancelText="Cancelar" confirmLoading={loading}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item name="currentPassword" label="Contraseña actual" rules={[{ required: true, message: 'Requerido' }]}>
          <Input.Password />
        </Form.Item>
        <Form.Item name="newPassword" label="Nueva contraseña" rules={[{ required: true }, { min: 6, message: 'Mínimo 6 caracteres' }]}>
          <Input.Password />
        </Form.Item>
        <Form.Item name="confirmPassword" label="Confirmar nueva contraseña" rules={[{ required: true, message: 'Requerido' }]}>
          <Input.Password />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function UserMenu() {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [pwModal, setPwModal] = useState(false);

  if (!user) return null;

  const roleLabel = user.rol === 'superadmin' ? 'Super Admin' : user.rol === 'editor' ? 'Editor' : 'Lector';
  const roleColor = user.rol === 'superadmin' ? 'gold' : user.rol === 'editor' ? 'blue' : 'green';

  const isLector = user.rol !== 'superadmin' && user.rol !== 'editor';

  const items = [
    ...(!isLector ? [{ key: 'info', label: <span style={{ color: '#888', fontSize: 12 }}>{roleLabel}</span>, disabled: true }, { type: 'divider' }] : []),
    ...(isSuperAdmin ? [{ key: 'seguridad', icon: <SafetyOutlined />, label: 'Seguridad', onClick: () => navigate('/seguridad') }] : []),
    ...(!isLector ? [{ key: 'password', icon: <KeyOutlined />, label: 'Cambiar contraseña', onClick: () => setPwModal(true) }] : []),
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Cerrar sesión', danger: true, onClick: () => { logout(); navigate('/'); } },
  ];

  return (
    <>
      <Dropdown menu={{ items }} placement="bottomRight" trigger={['click']}>
        <Button type="text" style={{ color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
          <UserOutlined />
          <span style={{ fontSize: 13 }}>{user.username}</span>
          {!isLector && <Tag color={roleColor} style={{ margin: 0, fontSize: 11 }}>{roleLabel}</Tag>}
        </Button>
      </Dropdown>
      <ChangePasswordModal open={pwModal} onClose={() => setPwModal(false)} />
    </>
  );
}

function AppLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const location = useLocation();
  const selectedKey = '/' + location.pathname.split('/')[1] || '/';
  const { user, logout, isSuperAdmin, canEdit } = useAuth();
  const navigate = useNavigate();
  const [pwModal, setPwModal] = useState(false);

  const closeDrawer = () => setDrawerOpen(false);

  const visibleMenuItems = navMenuItems.filter(item => !item.requireEdit || canEdit);

  const menuProps = {
    theme: 'dark', mode: 'inline', selectedKeys: [selectedKey],
    items: renderMenuItems(visibleMenuItems, isMobile ? closeDrawer : undefined, isSuperAdmin, canEdit)
  };

  const isLectorMobile = user?.rol !== 'superadmin' && user?.rol !== 'editor';

  const mobileUserActions = user ? (
    <div style={{ padding: '12px 16px', borderTop: '1px solid #003a6b' }}>
      <div style={{ color: '#aaa', fontSize: 12, marginBottom: 8 }}>
        {user.username}{!isLectorMobile && <> · <span style={{ color: user.rol === 'superadmin' ? '#ffd666' : '#91caff' }}>{user.rol === 'superadmin' ? 'Super Admin' : 'Editor'}</span></>}
      </div>
      {isSuperAdmin && (
        <Button type="text" size="small" icon={<SafetyOutlined />} style={{ color: '#aaa', display: 'block', width: '100%', textAlign: 'left', padding: '4px 0' }}
          onClick={() => { navigate('/seguridad'); closeDrawer(); }}>Seguridad</Button>
      )}
      {!isLectorMobile && (
        <Button type="text" size="small" icon={<KeyOutlined />} style={{ color: '#aaa', display: 'block', width: '100%', textAlign: 'left', padding: '4px 0' }}
          onClick={() => { setPwModal(true); closeDrawer(); }}>Cambiar contraseña</Button>
      )}
      <Button type="text" size="small" danger icon={<LogoutOutlined />} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '4px 0' }}
        onClick={() => { logout(); navigate('/'); closeDrawer(); }}><span style={{ marginLeft: 4 }}>Cerrar sesión</span></Button>
    </div>
  ) : null;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <>
          <Header className="mobile-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Button type="text" icon={<MenuOutlined style={{ fontSize: 20, color: '#fff' }} />} onClick={() => setDrawerOpen(true)} />
            <img src="https://nacional.uy/themes/custom/tcnf/logo.svg" alt="Nacional" style={{ height: 36, position: 'absolute', left: '50%', transform: 'translateX(-50%)' }} />
            <div id="mobile-header-actions" style={{ display: 'flex', alignItems: 'center', minWidth: 32, minHeight: 1 }} />
          </Header>
          <Drawer
            placement="left" open={drawerOpen} onClose={closeDrawer}
            styles={{ body: { padding: 0, background: '#001529', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }, header: { background: '#001529', borderBottom: '1px solid #003a6b' } }}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img src="https://nacional.uy/themes/custom/tcnf/logo.svg" alt="Nacional" style={{ height: 36 }} />
                <span style={{ color: '#fff', fontWeight: 'bold' }}>Nacional</span>
              </div>
            }
            width={260}
          >
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <Menu {...menuProps} />
            </div>
            {mobileUserActions}
          </Drawer>
          <Layout>
            <Content className="mobile-content"><Suspense fallback={<PageLoader />}><AppRoutes /></Suspense></Content>
          </Layout>
        </>
      ) : (
        <>
          <Sider collapsible>
            <div style={{ height: '64px', margin: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src="https://nacional.uy/themes/custom/tcnf/logo.svg" alt="Nacional" style={{ maxHeight: '48px', maxWidth: '100%' }} />
            </div>
            <Menu {...menuProps} />
          </Sider>
          <Layout>
            <Header style={{ background: '#003087', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#fff' }}>Club Nacional de Football</span>
              <UserMenu />
            </Header>
            <Content style={{ margin: '24px' }}><Suspense fallback={<PageLoader />}><AppRoutes /></Suspense></Content>
          </Layout>
        </>
      )}
      <ChangePasswordModal open={pwModal} onClose={() => setPwModal(false)} />
    </Layout>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/partidos" element={<Partidos />} />
      <Route path="/partidos/new" element={<PartidoForm />} />
      <Route path="/partidos/:id/edit" element={<PartidoForm />} />
      <Route path="/partidos/:id" element={<PartidoDetalle />} />
      <Route path="/jugadores" element={<Jugadores />} />
      <Route path="/jugadores/:id" element={<JugadorDetalle />} />
      <Route path="/jugadores-rivales" element={<JugadoresRivales />} />
      <Route path="/jugadores-rivales/:id" element={<JugadorRivalDetalle />} />
      <Route path="/dts-rivales" element={<DTsRivales />} />
      <Route path="/dts-rivales/:id" element={<DTRivalDetalle />} />
      <Route path="/personas" element={<Personas />} />
      <Route path="/personas/:id" element={<PersonaDetalle />} />
      <Route path="/equipos" element={<Equipos />} />
      <Route path="/equipos/:id" element={<EquipoDetalle />} />
      <Route path="/campeonatos" element={<Campeonatos />} />
      <Route path="/campeonatos/:id" element={<CampeonatoDetalle />} />
      <Route path="/paises" element={<Paises />} />
      <Route path="/paises/:id" element={<PaisDetalle />} />
      <Route path="/ciudades" element={<Ciudades />} />
      <Route path="/estadios" element={<Estadios />} />
      <Route path="/estadios/mapa" element={<EstadiosMapa />} />
      <Route path="/estadios/:id" element={<EstadioDetalle />} />
      <Route path="/arbitros" element={<Arbitros />} />
      <Route path="/arbitros/:id" element={<ArbitroDetalle />} />
      <Route path="/partidos-arbitro" element={<PartidosArbitro />} />
      <Route path="/directores-tecnicos" element={<DirectoresTecnicos />} />
      <Route path="/directores-tecnicos/atilio" element={<DirectoresTecnicosAtilio />} />
      <Route path="/directores-tecnicos/:id" element={<DTDetalle />} />
      <Route path="/motivos-partido" element={<MotivosPartido />} />
      <Route path="/posiciones" element={<Posiciones />} />
      <Route path="/nombres-campeonato" element={<NombresCampeonato />} />
      <Route path="/seguridad" element={<Seguridad />} />
      <Route path="/parametros" element={<Parametros />} />
      <Route path="/partidos-sin-atilio" element={<PartidosSinAtilio />} />
      <Route path="/asignar-campeonato-adicional" element={<AsignarCampeonatoAdicional />} />
      <Route path="/camisetas" element={<Camisetas />} />
      <Route path="/camisetas/:id" element={<CamisetaDetalle />} />
      <Route path="/urls-recursos" element={<RequireEditor><UrlsRecursos /></RequireEditor>} />
      <Route path="/ubicacion-estadios" element={<RequireEditor><UbicacionEstadios /></RequireEditor>} />
      <Route path="/marcas" element={<RequireEditor><Marcas /></RequireEditor>} />
      <Route path="/sponsors" element={<RequireEditor><Sponsors /></RequireEditor>} />
      <Route path="/asignar-sponsors" element={<RequireEditor><AsignarSponsors /></RequireEditor>} />
      <Route path="/asignar-camiseta" element={<RequireEditor><AsignarCamiseta /></RequireEditor>} />
      <Route path="/mantenimiento-recursos" element={<RequireEditor><MantenimientoRecursos /></RequireEditor>} />
      <Route path="/biblioteca" element={<Biblioteca />} />
      <Route path="/biblioteca/:id" element={<BibliotecaDetalle />} />
      <Route path="/biblioteca/:pubId/numeros/:numId" element={<BibliotecaNumeroDetalle />} />
    </Routes>
  );
}

function RequireEditor({ children }) {
  const { user, canEdit } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!canEdit) return <Navigate to="/" replace />;
  return children;
}

const PUBLIC_PATHS = ['/camisetas', '/biblioteca'];

function ProtectedLayout() {
  const { user, loginRequired } = useAuth();
  const location = useLocation();

  const isPublicPath = PUBLIC_PATHS.some(p =>
    location.pathname === p || location.pathname.startsWith(p + '/')
  );

  if (loginRequired && !user && !isPublicPath) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <AppLayout />;
}

function App() {
  return (
    <ConfigProvider locale={esES}>
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/*" element={<ProtectedLayout />} />
          </Routes>
        </AuthProvider>
      </Router>
    </ConfigProvider>
  );
}

export default App;
